import assert from "assert"
import { Identifier, NarrowedInterpretable } from "./interpretable"
import { InterpType } from "./interpretable";
import { MainTerminal } from "../Terminal"

type StackFrame = {
    identifiers: Record<string, Data>,
    name: string
}
export type InternalDataRepresentation = string | number | undefined | boolean | Data[] | string
export type DataType = 'string' | 'character' | 'integer' | 'float' | 'array' | 'boolean' | 'function' | 'unknown'
export interface Data {
    readonly data: InternalDataRepresentation
    readonly dataType: DataType
}



export type InterpreterStateCallback = (state: InterpreterState) => void;
export type NodeChangeCallback = (node: string, state: InterpreterState) => void;

/**
 * Handles both the stack frames and the current executing node.
 */
export class InterpreterState {

    private interpretables: Map<string, NarrowedInterpretable>;
    /**
     * The stack is a bunch of named variables. The topmost part of the stack is at index 0,
     * bottom of the stack is at index n.
     */
    private stack: StackFrame[] = [];


    private stackChangeListeners: InterpreterStateCallback[] = []
    public onStackChange(callback: InterpreterStateCallback) {
        this.stackChangeListeners.push(callback);
    }

    private executingNodeChangeListeners: NodeChangeCallback[] = []
    public onExecutingNodeChange(callback: NodeChangeCallback) {
        this.executingNodeChangeListeners.push(callback);
    }
    /**
     * A flag that is enabled when the program hits a return or break statement.
     * This is a signal to 1 or more parent interpretable to return with the return data it recieved from its
     * child.
     * Will be truthy if the program is currently returning, and falsy if not.
     * If it is returning, the return/break interpretable's uuid will be the value of isPreempted.
     * Different types of early returns will have different types once you get them from the map.
     * 
     * Example showing how the program is nested:
     * 
     * `Function -> Block -> If -> Block -> Return`
     * 
     * If the return is executed, this flag will signal to the Block to return early, which
     * signals the If, its Block, then to the Function, which sets the flag to
     * null again.
     * 
     */
    public isPreempted: string|null = null;
    constructor(interpretablesMap: Map<string, NarrowedInterpretable>) {
        this.interpretables = interpretablesMap;
    }

    /**
     * This sets the current line being executed in the GUI.
     * Since the user can control execution steps, any node that wants to highlight itself as
     * currently executing must await this.
     */
    setExecutingNode(node: string) {
        for (const callback of this.executingNodeChangeListeners) {
            callback(node, this);
        }
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(null)
            }, 500)
        })
    }
    /**
     * Push a frame onto the stack.
     * @param name name of the frame, displayed to users
     */
    stackPush(name: string) {
        this.stack.unshift({
            name: name,
            identifiers: {}
        });
        for (const callback of this.stackChangeListeners) {
            callback(this);
        }
    }

    /**
     * Pop a stack frame.
     */
    stackPop() {
        this.stack.shift();
        for (const callback of this.stackChangeListeners) {
            callback(this);
        }
    }
    /**
     * Gets the stack. To get one variable, use getIdentifier().
     * @returns the entire stack in readonly form.
     */
    getStack(): readonly StackFrame[] {
        return this.stack;
    }
    /**
     * Sets an identifier from the data in an interpretable.
     * @param interpretable an identifier or subscript.
     * @param data 
     */
    setIdentifier(interpretableUUID: string, data: Data) {
        assert(this.stack.length > 0, `attempt to set uuid ${interpretableUUID}, but no stack frames on the stack.`)
        var interpretable = this.interpretables.get(interpretableUUID);
        assert(interpretable, `attempted to setIdentifier from uuid ${interpretableUUID} but it was not in the interpretables array`);
        assert(interpretable.type == InterpType.Identifier, 'start of for loop is not an identifier or subscript (subscript not supported yet).');

        // replace with this when adding list sets:
        //         assert(interpretable.type == InterpType.Identifier || interpretable.type == InterpType.Subscript, 'start of for loop is not an identifier or subscript.');
        //const subscripts: Subscript[] = [];
        //while(interpretable.type == InterpType.Subscript) {
        //    subscripts.unshift(interpretable);
        //}
        //assert(interpretable.type == InterpType.Identifier, `subscript of something other than subscript or identifier: ${interpretable.type}`)
        
        
        // override data in any of the frames.
        for (const frame of this.stack) {
            if (frame.identifiers[interpretable.data.name]) {
                frame.identifiers[interpretable.data.name] = data;
                for (const callback of this.stackChangeListeners) {
                    callback(this);
                }
                return;
            }
        }
        // if it's not in the frame, set it on the top piece of the stack:
        this.stack[0].identifiers[interpretable.data.name] = data;
        for (const callback of this.stackChangeListeners) {
            callback(this);
        }
    }

    /**
     * As opposed to setting the identifier, which will traverse to lower frames in the stack, this
     * will add to the current scope. This is useful for function arguments that must be pushed
     * onto the current frame, rather than override anything on previous frames.
     * @param name name of the identifier
     * @param data data to add
     */
    setIdentifierInCurrentFrame(interpretableUUID: string, data: Data) {
        assert(this.stack.length > 0, `attempt to set uuid ${interpretableUUID}, but no stack frames on the stack.`)
        var interpretable = this.interpretables.get(interpretableUUID);
        assert(interpretable, `attempted to setIdentifier from uuid ${interpretableUUID} but it was not in the interpretables array`);
        assert(interpretable.type == InterpType.Identifier, 'start of for loop is not an identifier or subscript (subscript not supported yet).');

        assert(this.stack.length > 0, `attempt to set identifier ${interpretable.data.name} but no stack frames on the stack.`)
        assert(interpretable.data.name !== '', `name to receive the identifier is empty string`);
        this.stack[0].identifiers[interpretable.data.name] = data;
        for (const callback of this.stackChangeListeners) {
            callback(this);
        }
    }
    /**
     * Gets the current value of an identifier.
     * @param name name of the identifier.
     * @returns data of the identifier, which prefers values at the top of the stack.
     */
    getIdentifier(interpretableUUID: string): Data | undefined {
        assert(this.stack.length > 0, `attempt to set uuid ${interpretableUUID}, but no stack frames on the stack.`)
        var interpretable = this.interpretables.get(interpretableUUID);
        assert(interpretable, `attempted to setIdentifier from uuid ${interpretableUUID} but it was not in the interpretables array`);
        assert(interpretable.type == InterpType.Identifier, 'start of for loop is not an identifier or subscript (subscript not supported yet).');
        
        assert(interpretable.data.name !== '', `name to receive the identifier is empty string`);
        for (const frame of this.stack) {
            if (frame.identifiers[interpretable.data.name]) {
                return frame.identifiers[interpretable.data.name];
            }
        }
        return undefined;
    }
    /**
     * Recursively interpret a node.
     * @param interpretableUUID the node to execute
     * @returns any data it returns (if executing the root, it will likely return undefined.)
     */
    async interpret(interpretableUUID: string): Promise<Data | undefined> {
        console.log(this.interpretables)
        console.log(interpretableUUID)
        const interpretable = this.interpretables.get(interpretableUUID);
        /**
         * Hi this might look overwhelming! There's a lot of code in this function!
         * But the code is all seperated by this switch statement, so it's all disjoint. It does a
         * seperate thing for every interpretable type and there's no crossover.
         * You could break these into helper methods pretty easily if you wanted.
         */
        switch (interpretable.type) {
            case InterpType.ArrayLiteral: {
                const allData = await Promise.all(interpretable.children.map(child => this.interpret(child)));
                return {
                    data: allData,
                    dataType: 'array'
                };
            }
            case InterpType.Assignment: {
                // handles both `a = b` and `a[1] = b`, that's the main source of complexity here.
                await this.setExecutingNode(interpretableUUID);
                const right = await this.interpret(interpretable.children.right);
                assert(right !== undefined, `right of assignment was undefined`);
                this.setIdentifier(interpretable.children.left, right);
                return undefined;
            }
            case InterpType.BinaryGeneralOp: {
                const left = await this.interpret(interpretable.children.left);
                const right = await this.interpret(interpretable.children.right);
                switch (interpretable.data.operator) {
                    case "==":
                        return {
                            data: left.data == right.data,
                            dataType: 'boolean'
                        }
                    case "!=":
                        return {
                            data: left.data != right.data,
                            dataType: 'boolean'
                        }

                    case "or":
                        return {
                            data: left.data || right.data,
                            dataType: 'boolean'
                        }

                    case "and":
                        return {
                            data: left.data && right.data,
                            dataType: 'boolean'
                        }
                }
            }
            case InterpType.BinaryNumericalOp: {
                const left = await this.interpret(interpretable.children.left);
                const right = await this.interpret(interpretable.children.right);

                // REMOVED TO ALLOW STRING CONCAT!
                //assert(left.dataType == 'integer' || left.dataType == 'float', 'arithmetic operator, left not a number')
                //assert(right.dataType == 'integer' || right.dataType == 'float', 'arithmetic operator, left not a number')
                //assert(typeof (left.data) == "number", 'data/type mismatch')
                //assert(typeof (right.data) == "number", 'data/type mismatch')
                switch (interpretable.data.operator) {
                    case "<":
                        return {
                            data: left.data < right.data,
                            dataType: 'boolean'
                        }
                    case "<=":
                        return {
                            data: left.data <= right.data,
                            dataType: 'boolean'
                        }
                    case ">=":
                        return {
                            data: left.data >= right.data,
                            dataType: 'boolean'
                        }
                    case ">":
                        return {
                            data: left.data > right.data,
                            dataType: 'boolean'
                        }
                }
                // otherwise, mathematical operators:
                // use integer if both are integers, else float:
                const returnType = left.dataType == 'integer' && right.dataType == 'integer' ? 'integer' : 'float'
                switch(interpretable.data.operator) {
                    case "+":
                        return {
                            data: left.data + right.data,
                            dataType: returnType
                        }
                    case "-":
                        return {
                            data: left.data - right.data,
                            dataType: returnType
                        }
                    case "*":
                        return {
                            data: left.data * right.data,
                            dataType: returnType
                        }
                    case "/":
                        return {
                            data: left.data / right.data,
                            dataType: returnType
                        }
                    case "**":
                        return {
                            data: Math.pow(left.data, right.data),
                            dataType: returnType
                        }
                    case "%":
                        return {
                            data: left.data % right.data,
                            dataType: 'integer'
                        }
                }
            }
            case InterpType.Block: {
                if (!interpretable.parent) {
                    this.stackPush("global");
                }
                for (const node of interpretable.children) {
                    const nodeData = await this.interpret(node);
                    if (this.isPreempted) {
                        return nodeData;
                    }
                }
                if (!interpretable.parent) {
                    this.stackPop();
                }
                return;
            }
            case InterpType.Break:
                await this.setExecutingNode(interpretableUUID);
                this.isPreempted = interpretableUUID;
                return;
            case InterpType.Call:
                await this.setExecutingNode(interpretableUUID);
                const fnUUID = this.getIdentifier(interpretable.children.identifier);
                const identifier = this.interpretables.get(interpretable.children.identifier) as Identifier;

                // Hack! consider adding your own stuff.
                if(identifier.data.name == "print") {
                    assert(interpretable.children.arguments[0] && interpretable.children.arguments.length == 1, 'too many arguments for print');
                    MainTerminal.print((await this.interpret(interpretable.children.arguments[0]))?.data)
                    return;
                }
                if(identifier.data.name == "input") {
                    if(interpretable.children.arguments[0]) {
                        MainTerminal.print((await this.interpret(interpretable.children.arguments[0]))?.data)
                    }
                    return {
                        dataType: "string",
                        data: await MainTerminal.read()
                    }
                }


                assert(fnUUID, `identifier was not found`);
                assert(fnUUID.dataType == 'function', `identifier found but is not a function`)
                assert(typeof (fnUUID.data) == 'string');
                const fn = this.interpretables.get(fnUUID.data);
                assert(fn)
                assert(fn.type == InterpType.FunctionDef);

                this.stackPush(`${identifier.data.name}()`);
                assert(fn.children.parameters.length == fn.data.parameterTypes.length, 'mismatch of length of parameters and their types');
                assert(fn.children.parameters.length == interpretable.children.arguments.length, 'wrong number of parameters for the function');
                for (let i = 0; i < interpretable.children.arguments.length; i++) {
                    const interpretedArg = await this.interpret(interpretable.children.arguments[i]);
                    assert(interpretedArg !== undefined, `argument was found to be undefined`);
                    this.setIdentifierInCurrentFrame(fn.children.parameters[i], interpretedArg)
                }

                const returnData = await this.interpret(fn.children.body);
                if (this.isPreempted) {
                    assert(this.interpretables.get(this.isPreempted).type == InterpType.Return, 'broke out but nothing to break out of')
                    this.isPreempted = null;
                }
                this.stackPop();
                return returnData;
            case InterpType.DataLiteral:
                return {
                    data: interpretable.data.data,
                    dataType: interpretable.data.type
                }
            case InterpType.ForIter: {
                const iterable = (await this.interpret(interpretable.children.iterable)).data;
                assert(Array.isArray(iterable), `attempted to loop over a non-iterable.`);
                for (var i = 0; i < iterable.length; i++) {
                    await this.setExecutingNode(interpretableUUID);
                    this.setIdentifier(interpretable.children.identifier, iterable[i]);
                    const data = await this.interpret(interpretable.children.body);
                    // detect any preemption
                    if (this.isPreempted) {
                        if (this.interpretables.get(this.isPreempted).type == InterpType.Break) {
                            this.isPreempted = null;
                        }
                        return data;
                    }
                }
                return;
            }
            case InterpType.ForLoop: {
                const start: Data = interpretable.children.start ? await this.interpret(interpretable.children.start) : {
                    data: 0,
                    dataType: "integer"
                };
                this.setIdentifier(interpretable.children.identifier, start);
                var counter = (await this.getIdentifier(interpretable.children.identifier));
                var counterData = counter.data;
                assert(typeof(counterData) == 'number', 'counter not a number');
                var stop = (await this.interpret(interpretable.children.stop)).data;
                assert(typeof(stop) == 'number', 'stop not a number')

                await this.setExecutingNode(interpretableUUID);
                while (counterData < stop) {
                    const data = await this.interpret(interpretable.children.body);
                    // detect any preemption
                    if (this.isPreempted) {
                        if (this.interpretables.get(this.isPreempted).type == InterpType.Break) {
                            this.isPreempted = null;
                        }
                        return data;
                    }

                    const step: Data = interpretable.children.step ? (await this.interpret(interpretable.children.step)) : {
                        data: 1,
                        dataType: "integer"
                    };
                    const stepData = step.data;
                    assert(typeof(stepData) == 'number', `counter increment is not a number...`);
                    const sum = counterData + stepData;
                    // use integer if both are integers, else float:
                    this.setIdentifier(interpretable.children.identifier, {
                        dataType: counter.dataType == 'integer' && step.dataType == 'integer' ? 'integer' : 'float',
                        data: sum
                    });
                    counterData = (await this.getIdentifier(interpretable.children.identifier)).data;
                    assert(typeof(counterData) == 'number', 'counter is not a number')
                    var stop = (await this.interpret(interpretable.children.stop)).data;
                    assert(typeof(stop) == 'number', 'stop not a number')
                    if(counterData < stop) { // prevent redo. this is jank
                        await this.setExecutingNode(interpretableUUID);
                    }
                }

                return undefined;
            }
            case InterpType.FunctionDef: {
                this.setIdentifier(interpretable.children.identifier, {
                    data: interpretableUUID,
                    dataType: "function"
                })
                return;
            }
            case InterpType.If:
                await this.setExecutingNode(interpretableUUID);
                const condition = await this.interpret(interpretable.children.condition);
                // using JS 'truthy' comparisons
                if (condition.data) {
                    return await this.interpret(interpretable.children.then);
                } else if (interpretable.children.else) {
                    return await this.interpret(interpretable.children.else);
                }
                return;
            case InterpType.Return: {
                await this.setExecutingNode(interpretableUUID);
                let returnData = undefined;
                if (interpretable.children.withData) {
                    returnData = await this.interpret(interpretable.children.withData);
                }
                // put this after the interpret withData! In case the child has a return statement,
                // this.isPreempted would be overridden by it.
                this.isPreempted = interpretableUUID;
                return returnData;
            }
            case InterpType.Subscript:
                const array = await this.getIdentifier(interpretable.children.array);
                assert(array.dataType == "array" && Array.isArray(array.data), 'subscript - not an array')
                const subscript = await this.getIdentifier(interpretable.children.subscript);
                assert(subscript, 'subscript was undefined');
                assert(typeof (subscript.data) == "number", 'subscript was not a number');
                return array.data[subscript.data];
            case InterpType.UnaryOp: {
                const operand = await this.interpret(interpretable.children.operand);
                switch (interpretable.data.operator) {
                    case "!":
                        return {
                            data: !operand.data,
                            dataType: operand.dataType
                        }
                    case "-":
                        return {
                            data: -operand.data,
                            dataType: operand.dataType
                        }
                    case "+":
                        return {
                            data: +operand.data,
                            dataType: operand.dataType
                        }
                }
            }
            case InterpType.Identifier:
                return await this.getIdentifier(interpretableUUID);
            case InterpType.While: {
                let condition: Data | undefined;
                do {
                    await this.setExecutingNode(interpretableUUID);
                    condition = await this.interpret(interpretable.children.condition);
                    if (condition && condition.data) {
                        const data = await this.interpret(interpretable.children.body);
                        if (this.isPreempted) {
                            if (this.interpretables.get(this.isPreempted).type == InterpType.Break) {
                                this.isPreempted = null;
                            }
                            return data;
                        }
                    }
                } while (condition && condition.data)
            }
        }
    }
}